import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  FaExclamationTriangle,
  FaCheckCircle,
  FaLightbulb,
  FaDatabase,
  FaLayerGroup,
  FaChevronDown,
} from "react-icons/fa";
import { THEME } from "./theme";
import {
  cleanText,
  getFindings,
  getItemBody,
  getItemHeading,
  getItemPriority,
  getProductionJourney,
  getScoreColor,
  getScoreLabel,
  getStatusColors,
  isPhaseWiseReport,
  normalizePriority,
  normalizeScore,
  normalizeStatus,
} from "./reportUtils";

// The report card for the chat's "Reports & Analysis" branch (web port of
// ipoultry-mobile src/Report/ReportComponents.js).
//
// The card is built to answer the farmer's two questions at a glance:
// "how bad is it?" (status chip + zoned score bar + severity-graded warning
// stack, worst first) and "what do I do?" (numbered recommended actions).
// Sections collapse so a long LLM report stays scannable inside the widget.

// Severity is the item's OWN priority — the rail and chip agree, so a MEDIUM
// item never sits on a red rail. Amber/orange/red follow the day-grid scale.
const SEVERITY = {
  critical: { rail: "#DC2626", chipBg: "#FEE2E2", chipText: "#B91C1C", rank: 0 },
  high: { rail: "#EA580C", chipBg: "#FFEDD5", chipText: "#C2410C", rank: 1 },
  medium: { rail: "#F59E0B", chipBg: "#FEF3C7", chipText: "#B45309", rank: 2 },
  low: { rail: "#94A3B8", chipBg: "#F1F5F9", chipText: "#475569", rank: 3 },
  normal: { rail: "#94A3B8", chipBg: "#F1F5F9", chipText: "#475569", rank: 4 },
};

const severityOf = (item) => SEVERITY[normalizePriority(getItemPriority(item))] || SEVERITY.normal;

const sortBySeverity = (items) =>
  [...items].sort((a, b) => severityOf(a).rank - severityOf(b).rank);

// One collapsible section: icon + title + count, chevron toggles the body.
const Section = ({ Icon, title, count, defaultOpen = true, children }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ marginTop: 14 }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 7,
          width: "100%",
          background: "none",
          border: "none",
          padding: "0 0 6px",
          cursor: "pointer",
          textAlign: "left",
        }}
      >
        <Icon size={12} color="#64748b" />
        <span
          style={{
            fontSize: 12,
            fontWeight: 800,
            color: "#475569",
            textTransform: "uppercase",
            letterSpacing: 0.5,
          }}
        >
          {title}
        </span>
        <span
          style={{
            fontSize: 10.5,
            fontWeight: 800,
            color: "#64748b",
            background: "#f1f5f9",
            borderRadius: 8,
            padding: "1px 7px",
          }}
        >
          {count}
        </span>
        <span style={{ flex: 1 }} />
        <motion.span
          animate={{ rotate: open ? 0 : -90 }}
          style={{ display: "inline-flex", color: "#94a3b8" }}
        >
          <FaChevronDown size={11} />
        </motion.span>
      </button>
      {open && children}
    </div>
  );
};

// One report item: severity rail + heading + severity chip + body.
const ItemRow = ({ item, index, rail, chip, number }) => {
  const heading = cleanText(getItemHeading(item, index));
  const body = cleanText(getItemBody(item));
  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #e2e8f0",
        borderLeft: `3px solid ${rail}`,
        borderRadius: 10,
        padding: "8px 11px",
        marginBottom: 6,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 7, flexWrap: "wrap" }}>
        {number != null && (
          <span
            style={{
              flexShrink: 0,
              width: 18,
              height: 18,
              borderRadius: "50%",
              background: THEME.soft,
              color: THEME.blue,
              fontSize: 10.5,
              fontWeight: 800,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {number}
          </span>
        )}
        <span style={{ fontSize: 13, fontWeight: 700, color: THEME.ink }}>{heading}</span>
        {chip && (
          <span
            style={{
              fontSize: 10,
              fontWeight: 800,
              padding: "1px 8px",
              borderRadius: 8,
              background: chip.chipBg,
              color: chip.chipText,
              textTransform: "uppercase",
            }}
          >
            {chip.label}
          </span>
        )}
      </div>
      {body && body !== heading && (
        <div style={{ fontSize: 12.5, color: "#475569", marginTop: 2, lineHeight: 1.45 }}>
          {body}
        </div>
      )}
    </div>
  );
};

const ReportCard = ({ report, t }) => {
  if (!report || typeof report !== "object") return null;

  const rt = t.reportCard || {};
  const status = normalizeStatus(report.status);
  const sc = getStatusColors(status);
  const score = normalizeScore(report.production_score);
  const scoreColor = getScoreColor(score);
  const phaseWise = isPhaseWiseReport(report);
  const journey = getProductionJourney(report);
  const summary = cleanText(report.summary);
  const warnings = sortBySeverity(report.warnings || []);
  const findings = getFindings(report);
  const actions = report.recommended_actions || [];
  const dataQuality = report.data_quality || [];

  const period =
    report.start_date && report.end_date
      ? `${report.start_date} → ${report.end_date}`
      : report.analysis_date || null;

  const metaBits = [
    report.farm_name,
    report.batch_name,
    report.breed_name,
    report.current_day != null ? `${t.dayPrefix} ${report.current_day}` : null,
    period,
  ].filter(Boolean);

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      style={{
        background: "#fff",
        border: "1px solid #e2e8f0",
        borderTop: `3px solid ${sc.border}`,
        borderRadius: 14,
        padding: "13px 14px",
        marginBottom: 10,
        boxShadow: "0 2px 10px rgba(15,23,42,0.06)",
      }}
    >
      {/* Header */}
      <div style={{ display: "flex", alignItems: "flex-start", gap: 8, flexWrap: "wrap" }}>
        <div style={{ flex: 1, minWidth: 160 }}>
          <div style={{ fontSize: 15, fontWeight: 800, color: THEME.ink }}>
            {cleanText(report.heading) || rt.defaultHeading}
          </div>
          {metaBits.length > 0 && (
            <div style={{ fontSize: 11.5, color: "#64748b", marginTop: 3 }}>
              {metaBits.join(" · ")}
            </div>
          )}
        </div>
        <span
          style={{
            fontSize: 11,
            fontWeight: 800,
            padding: "3px 10px",
            borderRadius: 10,
            background: sc.background,
            border: `1px solid ${sc.border}`,
            color: sc.text,
            textTransform: "capitalize",
            flexShrink: 0,
          }}
        >
          {status}
        </span>
      </div>

      {/* Production score — zoned bar: <50 critical, 50–69 attention, 70+ good */}
      {report.production_score !== undefined && report.production_score !== null && (
        <div style={{ marginTop: 12 }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "baseline",
              marginBottom: 4,
            }}
          >
            <span style={{ fontSize: 12, fontWeight: 700, color: "#475569" }}>{rt.score}</span>
            <span style={{ fontSize: 15, fontWeight: 800, color: scoreColor }}>
              {score}
              <span style={{ fontSize: 11, fontWeight: 600, color: "#94a3b8" }}> /100</span>
              <span style={{ fontSize: 11, fontWeight: 700, marginLeft: 6 }}>
                {getScoreLabel(score)}
              </span>
            </span>
          </div>
          <div
            style={{
              position: "relative",
              height: 7,
              borderRadius: 4,
              background: "#eef2f7",
              overflow: "hidden",
            }}
          >
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${score}%` }}
              transition={{ duration: 0.6 }}
              style={{ height: "100%", borderRadius: 4, background: scoreColor }}
            />
            {/* Zone thresholds (50 / 70) so the number has context at a glance */}
            {[50, 70].map((tick) => (
              <span
                key={tick}
                style={{
                  position: "absolute",
                  left: `${tick}%`,
                  top: 0,
                  bottom: 0,
                  width: 2,
                  background: "#fff",
                }}
              />
            ))}
          </div>
        </div>
      )}

      {/* Summary */}
      {summary && (
        <div
          style={{
            marginTop: 12,
            background: "#f8fafc",
            border: "1px solid #eef2f7",
            borderRadius: 10,
            padding: "9px 11px",
            fontSize: 12.5,
            color: "#334155",
            lineHeight: 1.5,
          }}
        >
          {summary}
        </div>
      )}

      {/* Production journey (phase-wise reports) */}
      {phaseWise && journey.length > 0 && (
        <Section Icon={FaLayerGroup} title={rt.journey} count={journey.length}>
          {journey.map((item, i) => {
            const phaseStatus = normalizeStatus(item?.status);
            const pc = getStatusColors(phaseStatus);
            const body = cleanText(item?.description ?? item?.details ?? item?.action);
            return (
              <div key={i} style={{ display: "flex", gap: 8, marginBottom: 6 }}>
                <span style={{ flexShrink: 0, width: 8, borderRadius: 4, background: pc.border }} />
                <div
                  style={{
                    flex: 1,
                    background: pc.background,
                    border: `1px solid ${pc.border}`,
                    borderRadius: 10,
                    padding: "7px 10px",
                  }}
                >
                  <div style={{ fontSize: 12.5, fontWeight: 700, color: pc.text }}>
                    {cleanText(item?.phase) || `${rt.phase} ${i + 1}`}
                  </div>
                  {body && (
                    <div style={{ fontSize: 12, color: "#475569", marginTop: 2, lineHeight: 1.45 }}>
                      {body}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </Section>
      )}

      {/* Warnings — worst first, each on its own severity rail */}
      {warnings.length > 0 ? (
        <Section Icon={FaExclamationTriangle} title={rt.warnings} count={warnings.length}>
          {warnings.map((item, i) => {
            const sev = severityOf(item);
            const label = normalizePriority(getItemPriority(item));
            return (
              <ItemRow
                key={i}
                item={item}
                index={i}
                rail={sev.rail}
                chip={label !== "normal" ? { ...sev, label } : null}
              />
            );
          })}
        </Section>
      ) : (
        rt.noWarnings && (
          <div
            style={{
              marginTop: 14,
              display: "flex",
              alignItems: "center",
              gap: 7,
              background: "#F1FAF4",
              border: "1px solid #BBF7D0",
              borderRadius: 10,
              padding: "8px 11px",
              fontSize: 12.5,
              fontWeight: 600,
              color: "#15803D",
            }}
          >
            <FaCheckCircle size={13} /> {rt.noWarnings}
          </div>
        )
      )}

      {/* Key findings — positives, green rail */}
      {!phaseWise && findings.length > 0 && (
        <Section Icon={FaCheckCircle} title={rt.findings} count={findings.length}>
          {findings.map((item, i) => (
            <ItemRow key={i} item={item} index={i} rail="#16A34A" chip={null} />
          ))}
        </Section>
      )}

      {/* Recommended actions — a numbered checklist the farmer can work through */}
      {actions.length > 0 && (
        <Section Icon={FaLightbulb} title={rt.actions} count={actions.length}>
          {actions.map((item, i) => {
            const sev = severityOf(item);
            const label = normalizePriority(getItemPriority(item));
            return (
              <ItemRow
                key={i}
                item={item}
                index={i}
                number={i + 1}
                rail={label !== "normal" ? sev.rail : THEME.blue}
                chip={label !== "normal" ? { ...sev, label } : null}
              />
            );
          })}
        </Section>
      )}

      {/* Data quality — meta information, collapsed by default */}
      {dataQuality.length > 0 && (
        <Section Icon={FaDatabase} title={rt.dataQuality} count={dataQuality.length} defaultOpen={false}>
          {dataQuality.map((item, i) => (
            <ItemRow key={i} item={item} index={i} rail="#94A3B8" chip={null} />
          ))}
        </Section>
      )}
    </motion.div>
  );
};

export default ReportCard;
